import { useEffect, useState } from 'react'
import styled from 'styled-components'

import { api, LinkedInRepoSnapshot } from '../../../services/api'

/**
 * Star history for a repo, drawn from the daily snapshots.
 *
 * linkedin_repo_snapshots was filled every day and read by nothing, so a repo's
 * trajectory — the thing that says whether it is taking off — was invisible.
 */

const Wrap = styled.section`
  display: flex;
  flex-direction: column;
  gap: 0.4rem;
`

const Head = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: baseline;
  gap: 0.5rem;
`

const Label = styled.h4`
  margin: 0;
  font-size: 0.95rem;
`

const Delta = styled.span<{ $up: boolean }>`
  font-size: 0.8rem;
  font-weight: 600;
  font-variant-numeric: tabular-nums;
  color: ${({ $up }) => ($up ? '#2E7D32' : '#8A6D3B')};
`

const Muted = styled.p`
  opacity: 0.7;
  font-size: 0.85rem;
  margin: 0;
`

interface RepoHistoryProps {
    repoId: number
}

export const RepoHistory: React.FC<RepoHistoryProps> = ({ repoId }) => {
    const [points, setPoints] = useState<LinkedInRepoSnapshot[]>([])
    const [failed, setFailed] = useState(false)

    useEffect(() => {
        let cancelled = false
        api.linkedin
            .getRepoTimeseries(repoId, 90)
            .then((res) => !cancelled && setPoints(res.data.points))
            .catch(() => !cancelled && setFailed(true))
        return () => {
            cancelled = true
        }
    }, [repoId])

    if (failed) return <Muted>No se pudo cargar el histórico.</Muted>
    // One snapshot is a dot, not a trajectory: say so instead of drawing a
    // flat line that looks like "no growth".
    if (points.length < 2) {
        return <Muted>Sin histórico suficiente todavía (se guarda una instantánea al día).</Muted>
    }

    const stars = points.map((p) => p.stars ?? 0)
    const min = Math.min(...stars)
    const max = Math.max(...stars)
    const span = max - min || 1
    const width = 100
    const height = 28
    const path = stars
        .map((v, i) => {
            const x = (i / (stars.length - 1)) * width
            const y = height - ((v - min) / span) * height
            return `${i === 0 ? 'M' : 'L'}${x.toFixed(1)},${y.toFixed(1)}`
        })
        .join(' ')
    const growth = stars[stars.length - 1] - stars[0]

    return (
        <Wrap>
            <Head>
                <Label>Estrellas · últimos {points.length} días con datos</Label>
                <Delta $up={growth >= 0}>
                    {growth >= 0 ? '+' : ''}
                    {growth}
                </Delta>
            </Head>
            <svg
                viewBox={`0 0 ${width} ${height}`}
                preserveAspectRatio="none"
                style={{ width: '100%', height: 48 }}
                role="img"
                aria-label={`Evolución de estrellas: de ${stars[0]} a ${stars[stars.length - 1]}`}
            >
                <path d={path} fill="none" stroke="currentColor" strokeWidth={1.5} vectorEffect="non-scaling-stroke" />
            </svg>
            <Muted>
                {min} → {max} estrellas
            </Muted>
        </Wrap>
    )
}

export default RepoHistory
